import { Link, useLocation } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useAccessProfile } from "@/hooks/use-admin-auth";
import { CRM_TAB_TRIGGER, CRM_TABS_LIST } from "@/lib/crm/tabs";
import { settingsSections } from "@/lib/nav-visibility";
import { cn } from "@/lib/utils";

/**
 * The settings pages behind the sidebar's single "Asetukset" entry: a tab
 * row on each of them, with only the sections the user may open.
 */
export const SettingsTabs = (): ReactElement | null => {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const { profile } = useAccessProfile();
	const sections = profile ? settingsSections(profile) : [];
	const current = sections.find(
		(section) =>
			pathname === section.to || pathname.startsWith(`${section.to}/`),
	);
	if (!current || sections.length < 2) return null;
	return (
		<nav
			aria-label={t("admin:nav.settings")}
			className={cn(
				"mb-4 inline-flex items-center rounded-lg p-[3px]",
				CRM_TABS_LIST,
			)}
		>
			{sections.map((section) => (
				<Link
					key={section.to}
					to={section.to}
					aria-current={section === current ? "page" : undefined}
					data-state={section === current ? "active" : "inactive"}
					className={cn(
						"inline-flex h-full items-center gap-1.5 rounded-md font-medium text-sm",
						CRM_TAB_TRIGGER,
					)}
				>
					<section.icon className="h-4 w-4" />
					{t(section.labelKey)}
				</Link>
			))}
		</nav>
	);
};
